/**
 * Offscreen thumbnails for selection cards: a tiny shared renderer draws a
 * subject (ship layout or outfitted character) alone against the void and
 * hands back a data URL. Synchronous and cheap at card sizes; geometry the
 * builders allocate is disposed before returning.
 */
import * as THREE from "three";
import { buildShipFromLayout, disposeShip } from "../ship/parts";
import { Avatar } from "../character/avatar";
import type { CharacterStyle, ShipLayout } from "@hyperlight/shared";

const W = 168;
const H = 120;

let renderer: THREE.WebGLRenderer | null = null;

function render(frame: (scene: THREE.Scene) => { obj: THREE.Object3D; dispose: () => void }): string {
  renderer ??= new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(W, H);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x060b14);
  scene.add(new THREE.AmbientLight(0x8899bb, 1.4));
  const key = new THREE.DirectionalLight(0xfff4e0, 2.2);
  key.position.set(4, 6, 5);
  scene.add(key);

  const { obj, dispose } = frame(scene);
  // Frame the subject from a three-quarter angle by its bounding sphere.
  const box = new THREE.Box3().setFromObject(obj);
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const camera = new THREE.PerspectiveCamera(38, W / H, 0.1, 200);
  const dist = (sphere.radius / Math.tan((camera.fov * Math.PI) / 360)) * 1.25;
  camera.position
    .copy(sphere.center)
    .add(new THREE.Vector3(0.55, 0.4, 1).normalize().multiplyScalar(dist));
  camera.lookAt(sphere.center);

  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL();
  dispose();
  key.dispose();
  return url;
}

/** Card image for a saved ship layout. */
export function shipThumb(layout: ShipLayout): string {
  return render((scene) => {
    const built = buildShipFromLayout(layout, 0.58);
    scene.add(built.group);
    return { obj: built.group, dispose: () => disposeShip(built) };
  });
}

/** Card image for a saved outfit. */
export function outfitThumb(style: CharacterStyle): string {
  return render((scene) => {
    const avatar = new Avatar(scene);
    avatar.setStyle(style);
    avatar.group.visible = true;
    return { obj: avatar.group, dispose: () => avatar.dispose(scene) };
  });
}
